import type {
  SumerPositionInput,
  GroupAccumulator,
  SumerMarketMeta,
} from './types'

/**
 * Sumer absorption waterfall: computes total effective collateral in USD.
 *
 * For each group, collateral absorbs debt in priority order:
 *   1. cToken collateral absorbs suToken debt @ intraMintRate (highest)
 *   2. cToken collateral absorbs cToken debt   @ intraCRate
 *   3. suToken collateral absorbs remaining suToken debt @ intraSuRate
 *   4. suToken collateral absorbs remaining cToken debt  @ intraSuRate
 * Remaining collateral is valued at inter-group rates.
 */
export function computeSumerWaterfall(
  groups: SumerMarketMeta[],
  accum: GroupAccumulator,
): number {
  let total = 0

  for (const g of groups) {
    const a = accum[g.groupId]
    if (!a) continue

    let cDep = a.cDep
    let sDep = a.sDep
    let sDebt = a.sDebt
    let cDebt = a.cDebt

    // Phase 1: cToken collateral absorbs suToken debt @ intraMintRate
    if (cDep > 0 && sDebt > 0 && g.intraMintRate > 0) {
      const absorbed = Math.min(cDep * g.intraMintRate, sDebt)
      total += absorbed
      sDebt -= absorbed
      cDep -= absorbed / g.intraMintRate
    }

    // Phase 2: cToken collateral absorbs cToken debt @ intraCRate
    if (cDep > 0 && cDebt > 0 && g.intraCRate > 0) {
      const absorbed = Math.min(cDep * g.intraCRate, cDebt)
      total += absorbed
      cDebt -= absorbed
      cDep -= absorbed / g.intraCRate
    }

    // Phase 3: suToken collateral absorbs remaining suToken debt @ intraSuRate
    if (sDep > 0 && sDebt > 0 && g.intraSuRate > 0) {
      const absorbed = Math.min(sDep * g.intraSuRate, sDebt)
      total += absorbed
      sDebt -= absorbed
      sDep -= absorbed / g.intraSuRate
    }

    // Phase 4: suToken collateral absorbs remaining cToken debt @ intraSuRate
    if (sDep > 0 && cDebt > 0 && g.intraSuRate > 0) {
      const absorbed = Math.min(sDep * g.intraSuRate, cDebt)
      total += absorbed
      cDebt -= absorbed
      sDep -= absorbed / g.intraSuRate
    }

    // Remaining collateral valued at inter-group rates
    total += cDep * g.interCRate + sDep * g.interSuRate
  }

  return total
}

/**
 * Build group-level rate maps and accumulators from a position array.
 *
 * Returns:
 *  - groupRatesMap: unique SumerMarketMeta per groupId
 *  - gAccum: per-group accumulators (enabled collateral only)
 *  - gAccumAll: per-group accumulators (all protocol-active markets)
 */
export function buildSumerAccumulators(positions: SumerPositionInput[]): {
  groupRatesMap: Record<number, SumerMarketMeta>
  gAccum: GroupAccumulator
  gAccumAll: GroupAccumulator
} {
  const groupRatesMap: Record<number, SumerMarketMeta> = {}
  const gAccum: GroupAccumulator = {}
  const gAccumAll: GroupAccumulator = {}

  for (const pos of positions) {
    const sm = pos.sumerMeta
    const gId = sm.groupId

    if (!groupRatesMap[gId]) groupRatesMap[gId] = sm
    if (!gAccum[gId]) gAccum[gId] = { cDep: 0, sDep: 0, cDebt: 0, sDebt: 0 }
    if (!gAccumAll[gId])
      gAccumAll[gId] = { cDep: 0, sDep: 0, cDebt: 0, sDebt: 0 }

    const totalDebt = pos.debtUSD + pos.debtStableUSD

    if (sm.isCToken) {
      if (pos.collateralActive && pos.collateralEnabled)
        gAccum[gId].cDep += pos.depositsUSD
      if (pos.collateralActive) gAccumAll[gId].cDep += pos.depositsUSD
      gAccum[gId].cDebt += totalDebt
      gAccumAll[gId].cDebt += totalDebt
    } else {
      if (pos.collateralActive && pos.collateralEnabled)
        gAccum[gId].sDep += pos.depositsUSD
      if (pos.collateralActive) gAccumAll[gId].sDep += pos.depositsUSD
      gAccum[gId].sDebt += totalDebt
      gAccumAll[gId].sDebt += totalDebt
    }
  }

  return { groupRatesMap, gAccum, gAccumAll }
}

/**
 * Clone positions and apply a USD delta to the target market.
 */
export function applyPositionDelta(
  positions: SumerPositionInput[],
  targetMarketUid: string,
  depositsDeltaUSD: number,
  debtDeltaUSD: number,
  debtStableDeltaUSD: number,
): SumerPositionInput[] {
  return positions.map((p) => {
    if (p.marketUid !== targetMarketUid) return p
    return {
      ...p,
      depositsUSD: p.depositsUSD + depositsDeltaUSD,
      debtUSD: p.debtUSD + debtDeltaUSD,
      debtStableUSD: p.debtStableUSD + debtStableDeltaUSD,
    }
  })
}
